import { ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { isBrowser, isNonEmptyString, uniqBy } from '@sortlist-frontend/utils';
import { Kind } from 'graphql';

export const makeFragmentDeDupeLink = () => {
  return new ApolloLink((operation, forward) => {
    const definitions = uniqBy(operation.query.definitions, (def) => {
      if (def.kind === Kind.SCHEMA_DEFINITION) {
        return null;
      }
      if (def.kind === Kind.SCHEMA_EXTENSION) {
        return null;
      }
      return def.name?.value;
    });
    operation.query = {
      ...operation.query,
      // Remove duplicate fragment
      definitions,
    };
    return forward(operation).map((data) => {
      return data;
    });
  });
};

export const makeAuthMiddleware = (fetchAuthToken: () => Promise<string>) =>
  setContext(async () => {
    const token = await fetchAuthToken();

    if (token != null && isNonEmptyString(token)) {
      return {
        headers: {
          authorization: `Bearer ${token}`,
        },
      };
    }
  });

// A rejected Apollo operation (e.g. from `refetch`) whose `networkError` carries a 401
// status means the user's session/refresh token has expired — retrying immediately will
// fail identically until the user reloads or re-authenticates. The 401 can come from two
// different shapes: the GraphQL endpoint itself rejecting via `HttpLink` (a `ServerError`
// with `.statusCode`), or `makeAuthMiddleware`'s own token fetch rejecting before the
// request is even sent (a raw Axios error with `.response.status`, since `setContext`
// forwards whatever its setter throws as-is).
export const isUnauthorizedApolloError = (error: unknown): boolean => {
  const networkError = (error as { networkError?: { statusCode?: number; response?: { status?: number } } } | null)
    ?.networkError;
  return networkError?.statusCode === 401 || networkError?.response?.status === 401;
};

// Codes call sites already treat as expected and handle themselves (see
// useCaptureValidatePhoneOwnershipError), keyed by GraphQL operation name.
const IGNORED_GRAPHQL_ERROR_CODES: Record<string, number[]> = {
  ValidatePhoneOwnership: [60410, 60212, 60203, 60220, 60605],
  ConfirmPhoneOwnership: [60202],
};

// Like IGNORED_GRAPHQL_ERROR_CODES, but for errors with no bracketed Twilio code
// for getGraphQLErrorCode to extract — matched on the full message instead.
const IGNORED_GRAPHQL_ERROR_MESSAGES: Record<string, string[]> = {
  ConfirmPhoneOwnership: ['Phone owner invalid_code'],
};

export const getGraphQLErrorCode = (message: string | undefined): number | undefined => {
  const code = Number(message?.match(/(\d+)\s*:/)?.[1]);
  return isNaN(code) ? undefined : code;
};

export const isIgnoredGraphQLError = (operationName: string, message: string | undefined): boolean => {
  if (message != null && (IGNORED_GRAPHQL_ERROR_MESSAGES[operationName]?.includes(message) ?? false)) {
    return true;
  }
  const errorCode = getGraphQLErrorCode(message);
  return errorCode != null && (IGNORED_GRAPHQL_ERROR_CODES[operationName]?.includes(errorCode) ?? false);
};

// Sets the `Accept-Language` header from a caller-supplied locale getter, so the
// backend can localize server-driven labels. The source of the locale is the
// caller's concern (e.g. an app reading its own `me.locale` from the cache); this
// helper only formats the header and is a no-op when the getter returns nothing.
export const makeLocaleMiddleware = (getLocale: () => string | null) =>
  setContext((_operation, prevContext) => {
    if (!isBrowser()) return prevContext;
    const locale = getLocale();
    if (locale != null && isNonEmptyString(locale)) {
      // Merge into existing headers — returning a bare `{ headers }` replaces the
      // header map and would drop the Authorization header set upstream.
      return { ...prevContext, headers: { ...prevContext.headers, 'accept-language': locale } };
    }
    return prevContext;
  });

export const coreApiUrl = isBrowser()
  ? process.env.NEXT_PUBLIC_CORE_API_URL_CLOUDFLARE
  : process.env.NEXT_PUBLIC_CORE_API_URL_INTERNAL;

export const publicApiUrl = isBrowser()
  ? process.env.NEXT_PUBLIC_PUBLIC_API_BASE_URL_CLOUDFLARE
  : process.env.NEXT_PUBLIC_PUBLIC_API_BASE_URL_INTERNAL;

export const federationApiUrl = isBrowser()
  ? process.env.NEXT_PUBLIC_GRAPHQL_API_URL_CLOUDFLARE
  : process.env.NEXT_PUBLIC_GRAPHQL_API_URL_INTERNAL;
